import { useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { ResendTimer } from './ResendTimer';
import { useContainerWidth, useUser } from '@lendica/hooks';
import { maskEmail } from '@lendica/utils';

interface EmailVerificationDialogProps {
    open: boolean;
    onClose: () => void;
    onResend: () => void;
    onRefresh: () => void;
    isCooldown: boolean;
    cooldownMs: number;
    isSubmitting: boolean;
    rateLimitType?: string;
    hasRequested: boolean;
}

export function EmailVerificationDialog({
    open,
    onClose,
    onResend,
    onRefresh,
    isCooldown,
    cooldownMs,
    isSubmitting,
    rateLimitType,
    hasRequested,
}: EmailVerificationDialogProps) {
    const { isNarrow } = useContainerWidth();
    const user = useUser();
    const cooldownSeconds = useMemo(() => Math.max(0, Math.ceil(cooldownMs / 1000)), [cooldownMs]);

    const remainingSeconds = isCooldown ? cooldownSeconds : undefined;
    const resetOnLoad = typeof remainingSeconds === 'number';
    const isDailyLimit = rateLimitType === 'daily_limit';
    const resendDisabled = isSubmitting || isDailyLimit;
    const resendLabel = useMemo(() => {
        if (isSubmitting) return 'Sending…';
        return hasRequested ? 'Resend email' : 'Send verification email';
    }, [hasRequested, isSubmitting]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            aria-labelledby="email-verification-dialog-title"
        >
            <DialogTitle id="email-verification-dialog-title">
                Verify your email to continue
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <Typography>
                        {hasRequested
                            ? 'We just sent a link to the email address associated with your account. Please verify your email before inviting customers.'
                            : 'Send a verification link to the email address associated with your account.'}
                    </Typography>
                    <Typography>
                        {hasRequested
                            ? 'Once verified, come back to Daylit and continue where you left off.'
                            : 'Click “Send verification email” to get the link. Once verified, come back to Daylit to continue.'}
                    </Typography>
                    {user?.email && (
                        <Typography variant="body2" color="text.secondary">
                            Account email: <strong>{maskEmail(user?.email)}</strong>
                        </Typography>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions
                sx={{
                    px: 3,
                    pb: 2,
                    flexDirection: isNarrow ? 'column' : 'row',
                    alignItems: 'center',
                    justifyContent: isNarrow ? 'center' : 'flex-end',
                    gap: isNarrow ? 2 : 0,
                }}
            >
                <Button onClick={onClose} color="secondary">
                    Continue later
                </Button>
                <Button onClick={onRefresh} disabled={isSubmitting} variant="outlined">
                    I've verified
                </Button>
                <ResendTimer
                    seconds={remainingSeconds ?? 0}
                    resetOnLoad={resetOnLoad}
                    remainingSeconds={remainingSeconds}
                    onResend={onResend}
                    disabled={resendDisabled}
                    renderAs="button"
                    variant="contained"
                    color="primary"
                    sx={{ minWidth: 123 }}
                >
                    {resendLabel}
                </ResendTimer>
            </DialogActions>
        </Dialog>
    );
}
